import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDeletePart } from "@/api/equipment";
import { OfflineActionGuard } from "@/components/OfflineActionGuard";
import { OfflineUnavailable } from "@/components/OfflineUnavailable";
import { Button } from "@/components/ui/button";
import { isAllFarms, useFarmScope } from "@/hooks/useFarmScope";
import { useOfflineGuard } from "@/hooks/useOfflineGuard";
import { usePartsListData } from "@/hooks/usePartsListData";
import { PartList } from "./-components/PartList";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/parts/",
)({
	// Parts reference list works offline from the prepared parts dataset
	// (roadmap step 7): a mechanic looking up a part number or stock level in
	// the shed. Part detail (stock adjustment) stays online-only — a write,
	// conflict-prone per rule 2.
	staticData: { offline: "ready" },
	component: PartsListPage,
});

function PartsListPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const scope = useFarmScope();
	// Parts CRUD (create/delete) is online-only; the offline parts list is a
	// read-only reference (roadmap Tier 2).
	const { online, blocked } = useOfflineGuard();

	const { rows, summary, isLoading, isPrepared } = usePartsListData({
		orgId,
		scope,
	});

	const deleteMutation = useDeletePart();

	const handleDelete = (item: { id: string }) => {
		if (window.confirm(t("equipment.parts.confirmDelete"))) {
			deleteMutation.mutate({
				organizationId: orgId,
				partId: item.id,
			});
		}
	};

	// When offline and not prepared: clear unavailable state (no hang/error).
	if (!online && !isPrepared) {
		return <OfflineUnavailable variant="preparable" />;
	}

	return (
		<div className="flex flex-col gap-4 h-full">
			<div className="flex justify-end">
				<OfflineActionGuard blocked={blocked}>
					<Button
						aria-disabled={blocked}
						onClick={() =>
							navigate({
								to: "/app/org/$orgId/equipment/parts/new",
								params: { orgId },
							})
						}
					>
						<Plus className="h-4 w-4 mr-2" />
						{t("equipment.parts.create.title")}
					</Button>
				</OfflineActionGuard>
			</div>

			<div className="card bg-base-100 shadow-md flex-1">
				<div className="card-body">
					<PartList
						parts={rows}
						summary={summary}
						orgId={orgId}
						onDelete={handleDelete}
						isLoading={isLoading}
						canManage={online}
						showEstablishment={isAllFarms(scope)}
					/>
				</div>
			</div>
		</div>
	);
}
